import React, { createContext, useContext, useState, useCallback, useEffect, lazy, Suspense } from "react";

/**
 * The overlay owns the backdrop, the entrance/exit animation and the form
 * body — which between them pull in framer-motion, react-hook-form, zod and
 * the Supabase client. None of that is needed to render a page, so it is
 * fetched the first time a visitor opens the form.
 *
 * Once opened the overlay stays mounted and is driven by the `open` prop, so
 * its AnimatePresence exit animation still runs on close.
 */
const QuoteFormOverlay = lazy(() => import("@/components/QuoteFormOverlay"));

const QuoteFormContext = createContext<{ openQuoteForm: () => void }>({
  openQuoteForm: () => {},
});

export const useQuoteForm = () => useContext(QuoteFormContext);

export const QuoteFormProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [open, setOpen] = useState(false);
  /** Latches on first open so the overlay is never fetched on a page nobody opens it from. */
  const [everOpened, setEverOpened] = useState(false);

  const openQuoteForm = useCallback(() => {
    setEverOpened(true);
    setOpen(true);
  }, []);
  const closeQuoteForm = useCallback(() => setOpen(false), []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeQuoteForm();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, closeQuoteForm]);

  return (
    <QuoteFormContext.Provider value={{ openQuoteForm }}>
      {children}
      {everOpened && (
        <Suspense fallback={null}>
          <QuoteFormOverlay open={open} onClose={closeQuoteForm} />
        </Suspense>
      )}
    </QuoteFormContext.Provider>
  );
};

export default QuoteFormContext;
